import { useEffect, useId, useState } from 'react';

import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';

import { Field, FIELD_CONTROL_CLASS } from '../_shared/components';
import { copy } from './copy';

/**
 * Editing a season or an asset.
 *
 * **It happens in a dialog because the sitemap carries no route for either.**
 * The same choice the shelf surface made for the same reason: an in-place dialog
 * or no edit surface at all, and «management» is not satisfied by a list an
 * operator can only delete from.
 *
 * It edits exactly what the record holds — a title and a description — plus the
 * own-artwork, whose hint states that an asset without one inherits the title's.
 * One dialog serves every row: the heading and the subject are written from the
 * row that opened it, so the words stay in one place and cannot drift per row.
 */
export function RecordDialog({
  open,
  kind,
  subjectTitle,
  subjectDescription,
  busy,
  formError,
  onSave,
  onCancel,
}: {
  open: boolean;
  kind: 'group' | 'asset';
  subjectTitle: string;
  subjectDescription: string | null;
  busy: boolean;
  formError: string | null;
  onSave: (values: { title: string; description: string | null }) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(subjectTitle);
    setDescription(subjectDescription ?? '');
  }, [open, subjectTitle, subjectDescription]);

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'save') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of saving on the way out.
          if (isCancelledSubmit(event)) return;
          event.preventDefault();
          onSave({ title: title.trim(), description: description.trim() || null });
        }}
        className="flex flex-col gap-4"
      >
        <h2 id={headingId} className="text-h3">
          {kind === 'group' ? copy.dialog.groupTitle : copy.dialog.assetTitle}
        </h2>

        {formError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <Field label={copy.dialog.nameLabel}>
          {(id, describedBy) => (
            <input
              id={id}
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              aria-describedby={describedBy}
              className={FIELD_CONTROL_CLASS}
            />
          )}
        </Field>

        <Field label={copy.dialog.descriptionLabel}>
          {(id, describedBy) => (
            <textarea
              id={id}
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              aria-describedby={describedBy}
              className={`${FIELD_CONTROL_CLASS} min-h-[5rem] py-3`}
            />
          )}
        </Field>

        <div className="flex flex-wrap gap-2">
          {/*
            Both buttons in a `method="dialog"` form are submits, and a submit
            runs constraint validation — `formNoValidate` is what keeps the exit
            an exit.
          */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {copy.dialog.cancel}
          </button>
          <button
            type="submit"
            value="save"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.dialog.save}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
